// My Benefits — an employee's read-only view of their OWN benefits (allowances,
// deductions, retirement, plan enrollments). Self-enrollment is offered only when
// the tenant allows it. Visibility is gated by the admin "show_employee_view"
// toggle (the nav hides this page when off).
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Label } from '@/features/shared/components/ui/label';
import { HeartPulse, Wallet, MinusCircle, PiggyBank, ShieldCheck, Loader2, Plus } from 'lucide-react';
import {
  getBenefitsSettings, type BenefitsSettings,
  getMyAllowances, type EmployeeAllowance,
  getMyDeductions, type EmployeeDeduction,
  getMyRetirement, type RetirementContribution,
  getMyEnrollments, type BenefitEnrollment,
  listPlans, selfEnroll, type BenefitPlan,
} from '@/lib/hrApi';
import { MyMusicBenefitCard } from './MyMusicBenefitCard';

const fmt = (n: number, c = 'TZS') => `${c} ${Number(n || 0).toLocaleString()}`;
const COLOR: Record<string, string> = {
  active: 'bg-success-soft text-success-strong', approved: 'bg-success-soft text-success-strong',
  pending: 'bg-warning-soft text-warning-strong', draft: 'bg-slate-100 text-slate-600',
  declined: 'bg-danger-soft text-danger-strong', terminated: 'bg-danger-soft text-danger-strong',
  ended: 'bg-slate-100 text-slate-500', suspended: 'bg-warning-soft text-warning-strong',
};
const SB: React.FC<{ s: string }> = ({ s }) => <Badge className={`${COLOR[s] || 'bg-slate-100 text-slate-600'} border-0 capitalize`}>{s}</Badge>;
const sel = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';

export const MyBenefitsPage: React.FC = () => {
  const { t } = useTranslation('benefits');
  // ?enroll=<planId> deep-links here from My Products ("Enroll") to open the enrol dialog
  // pre-filled with the tenant's provisioned plan.
  const [params] = useSearchParams();
  const enrollPlanId = params.get('enroll') || '';
  const [settings, setSettings] = useState<BenefitsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [allow, setAllow] = useState<EmployeeAllowance[]>([]);
  const [deduct, setDeduct] = useState<EmployeeDeduction[]>([]);
  const [retire, setRetire] = useState<RetirementContribution[]>([]);
  const [enroll, setEnroll] = useState<BenefitEnrollment[]>([]);
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [planName, setPlanName] = useState<Record<string, string>>({});

  const loadMine = () => {
    getMyAllowances().then(setAllow).catch(() => {});
    getMyDeductions().then(setDeduct).catch(() => {});
    getMyRetirement().then(setRetire).catch(() => {});
    getMyEnrollments().then(setEnroll).catch(() => {});
  };
  useEffect(() => {
    getBenefitsSettings().then(setSettings).catch(() => setSettings(null)).finally(() => setLoading(false));
    loadMine();
    listPlans().then((p) => { setPlanName(Object.fromEntries(p.map((x) => [x.id, x.name]))); setPlans(p.filter((x) => x.status === 'active')); }).catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground p-8"><Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}</div>;

  if (settings && !settings.show_employee_view) {
    return <Card className="max-w-xl"><CardContent className="pt-6 text-sm text-muted-foreground">{t('hidden')}</CardContent></Card>;
  }

  const hasAny = allow.length + deduct.length + retire.length + enroll.length > 0;
  const canSelfEnroll = !!settings?.self_enrollment_enabled && !!settings?.benefits_enabled && plans.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-charcoal flex items-center gap-2"><HeartPulse className="w-6 h-6 text-orange-600" /> {t('my_title')}</h1>
        <p className="text-muted-foreground">{t('my_subtitle')}</p>
      </div>

      {/* Music benefit — renders only when the employee has one. */}
      <MyMusicBenefitCard />

      {!hasAny && (
        <Card><CardContent className="pt-6 text-sm text-muted-foreground">
          {t('none_recorded')}{canSelfEnroll ? t('none_enroll_hint') : ''}
        </CardContent></Card>
      )}

      {(settings?.benefits_enabled || enroll.length > 0) && (
        <EnrollCard rows={enroll} planName={planName} plans={plans} canEnroll={canSelfEnroll} initialPlanId={enrollPlanId} onChanged={loadMine} />
      )}

      {(allow.length > 0 || settings?.allowances_enabled) && (
        <SectionCard title={t('allowances')} icon={Wallet}>
          <Table>
            <TableHeader><TableRow><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_amount')}</TableHead><TableHead>{t('col_frequency')}</TableHead><TableHead>{t('col_status')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {allow.map((a) => <TableRow key={a.id}><TableCell className="font-medium">{a.name}</TableCell><TableCell className="capitalize">{a.allowance_type}</TableCell><TableCell>{fmt(a.amount, a.currency)}</TableCell><TableCell className="capitalize">{a.frequency.replace('_', ' ')}</TableCell><TableCell><SB s={a.status} /></TableCell></TableRow>)}
              {allow.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('no_allowances')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {(deduct.length > 0 || settings?.deductions_enabled) && (
        <SectionCard title={t('deductions')} icon={MinusCircle}>
          <Table>
            <TableHeader><TableRow><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_amount')}</TableHead><TableHead>{t('col_pretax')}</TableHead><TableHead>{t('col_status')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {deduct.map((d) => <TableRow key={d.id}><TableCell className="font-medium">{d.name}</TableCell><TableCell className="capitalize">{d.deduction_type.replace(/_/g, ' ')}</TableCell><TableCell>{fmt(d.amount, d.currency)}</TableCell><TableCell>{d.pre_tax ? t('yes') : t('no')}</TableCell><TableCell><SB s={d.status} /></TableCell></TableRow>)}
              {deduct.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('no_deductions')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {(retire.length > 0 || settings?.retirement_enabled) && (
        <SectionCard title={t('retirement')} icon={PiggyBank}>
          <Table>
            <TableHeader><TableRow><TableHead>{t('col_provider')}</TableHead><TableHead>{t('col_membership')}</TableHead><TableHead>{t('col_ee')}</TableHead><TableHead>{t('col_er')}</TableHead><TableHead>{t('col_status')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {retire.map((c) => <TableRow key={c.id}><TableCell className="font-medium">{c.retirement_provider}</TableCell><TableCell className="text-muted-foreground">{c.membership_number || '—'}</TableCell><TableCell>{c.employee_contribution_rate}</TableCell><TableCell>{c.employer_contribution_rate}</TableCell><TableCell><SB s={c.status} /></TableCell></TableRow>)}
              {retire.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('no_retirement')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </SectionCard>
      )}
    </div>
  );
};

const SectionCard: React.FC<{ title: string; icon: React.ComponentType<any>; children: React.ReactNode }> = ({ title, icon: Icon, children }) => (
  <Card>
    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Icon className="w-4 h-4 text-orange-600" /> {title}</CardTitle></CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const COVERAGE = ['employee_only', 'employee_spouse', 'employee_children', 'family'];
const EnrollCard: React.FC<{ rows: BenefitEnrollment[]; planName: Record<string, string>; plans: BenefitPlan[]; canEnroll: boolean; initialPlanId?: string; onChanged: () => void }> = ({ rows, planName, plans, canEnroll, initialPlanId, onChanged }) => {
  const { t } = useTranslation('benefits');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({ coverage_level: 'employee_only' });
  // Deep-link: when arriving with ?enroll=<planId> for an enrollable plan, open the dialog
  // pre-selected. Runs once the plan list has loaded so the id resolves to a real option.
  useEffect(() => {
    if (initialPlanId && canEnroll && plans.some((p) => p.id === initialPlanId)) {
      setF((prev: any) => ({ ...prev, benefit_plan_id: initialPlanId }));
      setOpen(true);
    }
  }, [initialPlanId, canEnroll, plans]);
  const save = async () => {
    if (!f.benefit_plan_id) return;
    setBusy(true);
    try { await selfEnroll({ benefit_plan_id: f.benefit_plan_id, coverage_level: f.coverage_level, effective_start_date: f.effective_start_date }); setOpen(false); setF({ coverage_level: 'employee_only' }); onChanged(); }
    catch (e) { toast.error(getApiErrorMessage(e, t('err_enroll', 'Could not enroll in the plan.')), { duration: 6000 }); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-orange-600" /> {t('plan_enrollments')}</CardTitle>
        {canEnroll && <Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> {t('enroll_in_plan')}</Button>}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_plan')}</TableHead><TableHead>{t('col_coverage')}</TableHead><TableHead>{t('col_status')}</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((e) => <TableRow key={e.id}><TableCell className="font-medium">{planName[e.benefit_plan_id] || e.benefit_plan_id.slice(0, 8)}</TableCell><TableCell className="capitalize">{e.coverage_level.replace(/_/g, ' ')}</TableCell><TableCell><SB s={e.status} /></TableCell></TableRow>)}
            {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t('not_enrolled')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('enroll_in_plan')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-xs">{t('col_plan')}</Label>
              <select className={sel} value={f.benefit_plan_id || ''} onChange={(e) => setF({ ...f, benefit_plan_id: e.target.value })}><option value="">{t('select_plan')}</option>{plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            </div>
            {/* What the selected plan covers + what it costs the employee — so they don't enrol blind. */}
            {(() => {
              const sp = plans.find((p) => p.id === f.benefit_plan_id);
              if (!sp) return null;
              return (
                <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1.5 text-xs">
                  {sp.description && <p className="leading-relaxed text-muted-foreground">{sp.description}</p>}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('your_premium', 'Your premium')}</span>
                    <span className="font-semibold text-foreground">{fmt(sp.employee_contribution)}<span className="font-normal text-muted-foreground"> /mo</span></span>
                  </div>
                  {sp.employer_contribution > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t('employer_contributes', 'Employer contributes')}</span>
                      <span className="font-medium text-foreground">{fmt(sp.employer_contribution)} /mo</span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="space-y-1"><Label className="text-xs">{t('col_coverage')}</Label>
              <select className={sel} value={f.coverage_level} onChange={(e) => setF({ ...f, coverage_level: e.target.value })}>{COVERAGE.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
            <Button onClick={save} disabled={busy || !f.benefit_plan_id}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('enroll')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MyBenefitsPage;
